import Authentication, { EFeature } from './Authentication';
import moment from 'moment';
import { GetCurrent } from 'services/user';
import { CurrentUserDto, EPolicy, UserRoleDto } from 'models/dtoTypes';
import { get } from 'lodash';
import SmeConfig from 'data/SmeConfig';

// function deleteAllCookies() {
//   const cookies = document.cookie.split(';');
//
//   for (let i = 0; i < cookies.length; i++) {
//     const cookie = cookies[i];
//     const eqPos = cookie.indexOf('=');
//     const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
//     document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
//   }
// }

class CurrentUser implements Authentication {
  private localStorage: Storage;
  static instance: Authentication | undefined;

  constructor() {
    this.localStorage = localStorage;
  }

  static getInstance(): Authentication {
    if (!CurrentUser.instance) {
      CurrentUser.instance = new CurrentUser();
    }
    return CurrentUser.instance;
  }

  isValid(aadUserId?: string): boolean {
    const previousAadUserId = this.getAadUserId();
    const currentUserCachedAt = this.localStorage.getItem('userCachedAt');
    if (!previousAadUserId || !currentUserCachedAt) {
      return false;
    }

    // aadUserId is changed if aadUserId is passed in
    if (aadUserId && previousAadUserId !== aadUserId) {
      return false;
    }

    return moment(currentUserCachedAt) > moment().subtract(2, 'hours');
  }

  clearSession(): void {
    this.localStorage.clear();
  }

  getLocale(): string {
    const locale = this.localStorage.getItem('locale') as string;
    return locale || 'en-GB';
  }

  setLocale(locale: string): void {
    this.localStorage.setItem('locale', locale);
  }

  getCompanyName(): string {
    const userStr = this.localStorage.getItem('user') as string;
    try {
      const user = JSON.parse(userStr);
      return (get(user, ['company', 'name']) as string) || '';
    } catch (e) {
      return '';
    }
  }

  getAadUserId(): string {
    const userStr = this.localStorage.getItem('user') as string;
    try {
      const user = JSON.parse(userStr);
      return (get(user, ['aadUserId']) as string) || '';
    } catch (e) {
      return '';
    }
  }

  getOrganisationId(): string {
    const userStr = this.localStorage.getItem('user') as string;
    try {
      const user = JSON.parse(userStr);
      return (get(user, ['organisationId']) as string) || '';
    } catch (e) {
      return '';
    }
  }

  getOrganisationAttr(attr: string): string {
    const userStr = this.localStorage.getItem('user') as string;
    try {
      const user = JSON.parse(userStr);
      return (get(user, ['organisation', attr]) as string) || '';
    } catch (e) {
      return '';
    }
  }

  getCompanyAttr(attr: string): string {
    const userStr = this.localStorage.getItem('user') as string;
    try {
      const user = JSON.parse(userStr);
      return (get(user, ['company', attr]) as string) || '';
    } catch (e) {
      return '';
    }
  }

  setUser(userJsonString: string): void {
    this.localStorage.setItem('user', userJsonString);
    this.localStorage.setItem('userCachedAt', new Date().toISOString());
  }

  async refresh(config?: SmeConfig): Promise<CurrentUserDto | undefined> {
    const user = await GetCurrent();
    const setCurrentUserDto = config?.setCurrentUserDto;
    if (user) {
      const userStr = JSON.stringify(user);
      this.localStorage.setItem('user', userStr);
      setCurrentUserDto && setCurrentUserDto(user);
      return user;
    }
    return undefined;
  }

  getRole(): string | undefined {
    const role = this.localStorage.getItem('role') as string;
    return role ?? undefined;
  }

  setRole(role: string): void {
    this.localStorage.setItem('role', role);
  }

  // Cache user preference
  getScope(key: string): string {
    const scope = this.localStorage.getItem(key) as string;
    return scope || '';
  }

  setScope(key: string, value: string): void {
    this.localStorage.setItem(key, value);
  }

  isAdmin(): boolean {
    let userRole = '';
    const userStr = this.localStorage.getItem('user') as string;
    try {
      const user = JSON.parse(userStr);
      userRole = (get(user, ['userRole']) as string) || '';
    } catch (e) {
      userRole = '';
    }
    return userRole === UserRoleDto.Admin;
  }

  canManageResource(feature: EFeature): boolean {
    const userStr = this.localStorage.getItem('user') as string;
    try {
      const user = JSON.parse(userStr);
      const resources = (get(user, ['resources']) || []) as string[];
      if (feature === EFeature.user) {
        return resources.includes('SetupUser' as EPolicy);
      }
      if (feature === EFeature.companyList) {
        return resources.includes('FuncBulk' as EPolicy);
      }
      if (feature === EFeature.organisation) {
        return resources.includes('SetupOrganisation' as EPolicy);
      }
      if (feature === EFeature.scoreCompany) {
        return resources.includes(EPolicy.ScoreCompany);
      }
      if (feature === EFeature.smartGovernance) {
        return resources.includes(EPolicy.SgRespond) || resources.includes(EPolicy.SgAssign);
      }
      if (feature === EFeature.assignSg) {
        return resources.includes(EPolicy.SgAssign);
      }
      return false;
    } catch (e) {
      return false;
    }
  }
}

export default CurrentUser;
